import { useEffect, useRef } from 'react';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useSelfRank } from './useSelfRank';
import { useXpSummary } from './useXpSummary';
import { maybeCelebrateRank } from '../helpers/coinRankCelebrations.helper';

/**
 * Fires the Badge Forge rank-up ceremony when the user's coin-derived rank rises
 * (specs/coin-based-ranks AC-6/AC-7). Mounted once via CelebrationHost. Driven by the coin store
 * (useSelfRank), so it fires on coin gains that award NO XP (daily claim, coupon, purchase) — which
 * never refetch the XP summary. The XP `level` is read only to label the ceremony chip; it does not
 * gate the fire. Each distinct rank value is handled once per session.
 */
export function useCoinRankCelebrations(): void {
  const { user } = useSupabaseAuth();
  const userId = user?.id ?? null;
  const { rankIndex, effectivePeak } = useSelfRank();
  const { data } = useXpSummary();
  const level = data?.level ?? 1;
  const lastHandledRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userId || rankIndex == null) return;
    const signature = `${userId}:${rankIndex}`;
    if (lastHandledRef.current === signature) return;
    lastHandledRef.current = signature;
    void maybeCelebrateRank(userId, rankIndex, effectivePeak, level);
  }, [userId, rankIndex, effectivePeak, level]);
}
